import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@talentech/components";

type JobAdPage = {
  job_ad_page_id: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
};

export type CustomerDomain = {
  domain_name: string;
  domain_url: string;
  domain_id: string;
  domain_type: "custom" | "hosted";
  customer_alias?: string | null;
  public_url?: string | null;
  language: string;
  embedded_in_iframe?: boolean;
  joblist_page: {
    joblist_page_id: string;
    domain_id: string;
    created_at: string;
    updated_at: string;
    updated_by: string;
    job_ad_pages: JobAdPage[];
  };
};

export interface CustomerConfig {
  customer_id: string;
  name: string;
  created_date: string;
  updated_by: string;
  updated_date: string;
  domains: CustomerDomain[];
  settings: CustomerSettings;
}

export type CustomerSettings = {
  privacy: {
    analytics: {
      ontame: boolean;
    },
    cookies: {
      cookiebot: {
        cid: string;
        enable: boolean;
      }
    }
  };
};

export type CustomerDomainPayload = {
  domain_type: "custom" | "hosted";
  domain_name: string;

  domain_url?: string;
  public_url?: string;
  customer_alias?: string;
  is_primary?: boolean;

  tenantId: string;
};

export type DomainAddressPayload = {
  tenant_id: string;
  domain_id: string;
  customer_alias?: string;
  domain_name?: string;
};

type AssignPagePayload = {
  domain_id: string;
  joblist_page_id: string;
  tenant_id: string;
};

export const customersApi = createApi({
  reducerPath: "customers",
  baseQuery: axiosBaseQuery({ baseUrl: process.env.API_URL as string }),
  tagTypes: ["CustomerConfig"],
  endpoints: (builder) => ({
    getCustomerConfig: builder.query<CustomerConfig, { tenantId: string }>({
      query: (params) => ({
        url: `/customers/${params.tenantId}/config`,
      }),
      providesTags: ["CustomerConfig"],
    }),

    updateCustomerConfig: builder.mutation<any, { tenantId: string; settings: CustomerSettings }>({
      query: ({ tenantId, settings }) => ({
        url: `/customers/${tenantId}/settings`,
        method: "PUT",
        data: settings,
      }),
      invalidatesTags: ["CustomerConfig"],
    }),

    addCustomerDomain: builder.mutation<any, CustomerDomainPayload>({
      query: ({ tenantId, ...data }) => ({
        url: `/customers/${tenantId}/domains`,
        method: "POST",
        data,
      }),
      invalidatesTags: ["CustomerConfig"],
    }),

    deleteCustomerDomain: builder.mutation<
      any,
      { domain_id: string; tenant_id: string }
    >({
      query: ({ domain_id, tenant_id }) => ({
        url: `/customers/${tenant_id}/domains/${domain_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomerConfig"],
    }),

    addJobListPageToDomain: builder.mutation<any, AssignPagePayload>({
      query: ({ tenant_id, ...data }) => ({
        url: `/customers/${tenant_id}/domains/${data.domain_id}/joblist`,
        method: "POST",
        data,
      }),
      invalidatesTags: ["CustomerConfig"],
    }),

    deleteJobListPageFromDomain: builder.mutation<any, AssignPagePayload>({
      query: ({ joblist_page_id, tenant_id, domain_id }) => ({
        url: `/customers/${tenant_id}/domains/${domain_id}/joblist/${joblist_page_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomerConfig"],
    }),

    addJobAdPageToDomain: builder.mutation<
      any,
      AssignPagePayload & {
        job_ad_page_id: string;
      }
    >({
      query: ({ domain_id, tenant_id, ...data }) => ({
        url: `/customers/${tenant_id}/domains/${domain_id}/joblist/${data.joblist_page_id}/jobad`,
        method: "POST",
        data,
      }),
      invalidatesTags: ["CustomerConfig"],
    }),

    deleteJobAdPageFromDomain: builder.mutation<
      any,
      AssignPagePayload & {
        job_ad_page_id: string;
      }
    >({
      query: ({ job_ad_page_id, joblist_page_id, tenant_id, domain_id }) => ({
        url: `/customers/${tenant_id}/domains/${domain_id}/joblist/${joblist_page_id}/jobad/${job_ad_page_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomerConfig"],
    }),

    updateDomainLanguage: builder.mutation<any, { domain_id: string; language: string; tenant_id: string }>({
      query: ({ domain_id, language, tenant_id }) => ({
        url: `/customers/${tenant_id}/domains/${domain_id}/language`,
        method: "PATCH",
        params: { language },
      }),
      invalidatesTags: ["CustomerConfig"],
    }),

    updateDomainEmbeddedInIframe: builder.mutation<
      unknown,
      { domain_id: string; embedded_in_iframe: boolean; tenant_id: string }
    >({
      query: ({ domain_id, embedded_in_iframe, tenant_id }) => ({
        url: `/customers/${tenant_id}/domains/${domain_id}/embedded-in-iframe`,
        method: "PATCH",
        params: { embedded_in_iframe },
      }),
      invalidatesTags: ["CustomerConfig"],
    }),

    updateDomainAddress: builder.mutation<any, DomainAddressPayload>({
      query: ({ tenant_id, domain_id, ...data }) => ({
        url: `/customers/${tenant_id}/domains/${domain_id}/address`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["CustomerConfig"],
    }),
  }),
});

export const {
  useGetCustomerConfigQuery,
  useAddCustomerDomainMutation,
  useAddJobListPageToDomainMutation,
  useAddJobAdPageToDomainMutation,

  useDeleteCustomerDomainMutation,
  useDeleteJobAdPageFromDomainMutation,
  useDeleteJobListPageFromDomainMutation,

  useUpdateDomainLanguageMutation,
  useUpdateDomainEmbeddedInIframeMutation,
  useUpdateDomainAddressMutation,
  useUpdateCustomerConfigMutation,
} = customersApi;
